import { Helmet } from "react-helmet";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useScrollAnimation, fadeInUp, AnimatedDiv, AnimatedH2 } from "@/hooks/useScrollAnimation";

const faqs = [
  {
    q: "What areas of California do you serve for farm and land sales?",
    a: "We work throughout California's Central Valley, with the deepest coverage in Merced, Madera, and Fresno counties. We also assist buyers and sellers in surrounding agricultural areas including Stanislaus, Kings, and Tulare counties.",
  },
  {
    q: "How much does farmland cost in the Central Valley?",
    a: "Pricing depends heavily on water access, soil class, crop type, and age of the planting. Mature almond and pistachio orchards typically command a premium over open ground, while row-crop and pasture land sits at the lower end. We provide a property-specific valuation before you make an offer or list.",
  },
  {
    q: "Do you help buyers evaluate water rights before purchase?",
    a: "Yes. Water is the single biggest driver of farmland value in the Valley. Every property we take a buyer through includes a review of well depth and output, irrigation district access, and SGMA groundwater sustainability plan implications for that basin.",
  },
  {
    q: "Should I plant almonds or pistachios?",
    a: "Pistachios tolerate saline soil and lower water quality better and have a longer productive life, while almonds reach full production sooner. The right answer depends on your soil, water, and how long you plan to hold the property — we walk through the economics of both with every orchard buyer.",
  },
  {
    q: "Can you manage a farm if I don't live in California?",
    a: "Yes. Absentee ownership is a large part of our farm management business. We handle crop planning, irrigation, spraying and harvest coordination, vendor management, and regular reporting so out-of-state owners stay informed without being on site.",
  },
  {
    q: "How long does it take to sell a farm?",
    a: "Well-priced Central Valley farm properties with solid water typically go under contract within a few months, though larger operations and specialty properties can take longer. Timing your listing to the crop and lending cycle makes a measurable difference in the final price.",
  },
];

const FAQ = () => {
  const { ref, controls } = useScrollAnimation();

  const schema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <section id="faq" className="py-20 md:py-28 bg-background">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>
      <div className="container mx-auto px-4 md:px-6 max-w-3xl">
        <AnimatedDiv ref={ref} initial="hidden" animate={controls} variants={fadeInUp} className="text-center mb-12">
          <p className="text-sm font-medium text-primary tracking-[0.3em] uppercase mb-4">Common Questions</p>
          <AnimatedH2 variants={fadeInUp} className="text-3xl md:text-5xl heading-luxury text-foreground mb-4">
            Farm &amp; Land FAQs
          </AnimatedH2>
          <div className="section-divider mt-4"></div>
        </AnimatedDiv>

        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-lg font-semibold text-foreground">
                {faq.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground leading-relaxed text-base">
                {faq.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        <p className="text-center text-muted-foreground mt-10">
          Want the full breakdown?{" "}
          <a
            href="/guides/buying-a-farm-central-valley"
            className="text-primary font-semibold underline underline-offset-4"
          >
            Read our complete guide to buying a farm in the Central Valley
          </a>
          .
        </p>
      </div>
    </section>
  );
};

export default FAQ;
